import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faShoppingCart,
  faUsers,
  faMoneyBillWave,
  faExclamationCircle,
  faArrowRight,
  faEye,
  faIndustry,
  faChartPie,
  faClock,
  faTags,
  faLayerGroup,
  faRulerVertical
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useAuthStore } from "../../store/slice/authSlice";

const Dashboard = () => {
  const { userInfo } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalProducts: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    recentOrders: [],
    productPerformance: [],
    recentQueries: [],
    inventoryAlerts: [],
  });

  useEffect(() => {
    // This would be replaced with actual API calls
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Simulate API calls with setTimeout
        setTimeout(() => {
          setDashboardData({
            totalProducts: 32,
            totalOrders: 187,
            pendingOrders: 14,
            totalRevenue: 146750,
            recentOrders: [
              { id: "ORD-7128", customer: "Fashion Boutique", date: "2023-06-15", amount: 8200, status: "delivered" },
              { id: "ORD-7124", customer: "Apparel Makers Ltd", date: "2023-06-14", amount: 12150, status: "processing" },
              { id: "ORD-7122", customer: "Trendy Garments", date: "2023-06-12", amount: 5600, status: "pending" },
              { id: "ORD-7118", customer: "Elite Clothing", date: "2023-06-10", amount: 7250, status: "delivered" },
            ],
            productPerformance: [
              { id: "FAB-001", name: "Premium Cotton Fabric", sold: 420, revenue: 63000, stock: 850 },
              { id: "FAB-012", name: "Silk Blend", sold: 180, revenue: 36000, stock: 120 },
              { id: "FAB-023", name: "Denim Material", sold: 350, revenue: 52500, stock: 210 },
              { id: "FAB-035", name: "Polyester Blend", sold: 520, revenue: 41600, stock: 160 },
            ],
            recentQueries: [
              { id: "QRY-425", customer: "Fashion Boutique", subject: "Fabric Durability Specifications", status: "pending", date: "2023-06-15" },
              { id: "QRY-424", customer: "Apparel Makers Ltd", subject: "Bulk Order for Summer Collection", status: "resolved", date: "2023-06-12" },
            ],
            inventoryAlerts: [
              { id: "FAB-012", name: "Silk Blend", stock: 120, threshold: 200 },
              { id: "FAB-035", name: "Polyester Blend", stock: 160, threshold: 250 },
            ],
          });
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Dashboard Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white pt-8 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold">Fabric Manufacturer Dashboard</h1>
              <p className="mt-2 text-indigo-100">
                Welcome back, {userInfo?.companyName || "Fabric Industries Ltd"}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <button className="bg-white text-indigo-800 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all hover:bg-indigo-50">
                <FontAwesomeIcon icon={faEye} />
                <span>View Catalog</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Fabric Varieties"
            value={dashboardData.totalProducts}
            icon={faLayerGroup}
            iconBg="bg-indigo-100"
            iconColor="text-indigo-600"
            trend="+5.2%"
            trendUp={true}
          />
          <StatCard
            title="Total Orders"
            value={dashboardData.totalOrders}
            icon={faShoppingCart}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            trend="+9.4%"
            trendUp={true}
          />
          <StatCard
            title="Pending Orders"
            value={dashboardData.pendingOrders}
            icon={faClock}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            trend="-2.1%"
            trendUp={false}
          />
          <StatCard
            title="Total Revenue"
            value={formatCurrency(dashboardData.totalRevenue)}
            icon={faMoneyBillWave}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            trend="+11.7%"
            trendUp={true}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          {/* Recent Orders Section - 2/3 width on large screens */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faShoppingCart} className="mr-3 text-purple-500" />
                    Recent Orders
                  </h2>
                  <Link to="/orders" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
                    View All
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </Link>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.recentOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.customer}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{formatCurrency(order.amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'processing' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-amber-100 text-amber-800'
                            }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button className="text-indigo-600 hover:text-indigo-900 font-medium">View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Product Performance */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden mt-6">
              <div className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faChartLine} className="mr-3 text-indigo-500" />
                    Fabric Performance
                  </h2>
                  <Link to="/products" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
                    View All
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </Link>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fabric</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meters Sold</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dashboardData.productPerformance.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.sold} meters</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{formatCurrency(product.revenue)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.stock > 200 ? 'bg-green-100 text-green-800' :
                            product.stock > 100 ? 'bg-amber-100 text-amber-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                            {product.stock} meters
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button className="text-indigo-600 hover:text-indigo-900 font-medium">View</button>
                            <button className="text-gray-600 hover:text-gray-900">Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Sidebar - 1/3 width on large screens */}
          <div className="space-y-6">
            {/* Inventory Alerts */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-red-50">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                    <FontAwesomeIcon icon={faExclamationCircle} className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-lg font-medium text-gray-900">Low Stock Alerts</h2>
                    <p className="text-sm text-gray-500">Fabrics that need restocking</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5 divide-y divide-gray-200">
                {dashboardData.inventoryAlerts.map((alert) => (
                  <div key={alert.id} className="py-4 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{alert.name}</p>
                      <p className="text-sm text-red-600">
                        <span className="font-medium">{alert.stock}</span> meters in stock
                        (Threshold: {alert.threshold})
                      </p>
                    </div>
                    <Link
                      to={`/products/edit/${alert.id}`}
                      className="text-sm text-indigo-600 font-medium hover:text-indigo-800"
                    >
                      Restock
                    </Link>
                  </div>
                ))}
                {dashboardData.inventoryAlerts.length === 0 && (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No inventory alerts at this time
                  </div>
                )}
              </div>
            </div>

            {/* Recent Queries */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faUsers} className="mr-3 text-amber-500" />
                    Recent Inquiries
                  </h2>
                  <Link to="/queries" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center">
                    View All
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </Link>
                </div>
              </div>
              <div className="px-6 py-5 divide-y divide-gray-200">
                {dashboardData.recentQueries.map((query) => (
                  <div key={query.id} className="py-4">
                    <div className="flex justify-between">
                      <p className="text-sm font-medium text-gray-900">{query.subject}</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${query.status === 'resolved' ? 'bg-green-100 text-green-800' :
                        'bg-amber-100 text-amber-800'
                        }`}>
                        {query.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">From: {query.customer}</p>
                    <p className="text-xs text-gray-400 mt-1">{query.date}</p>
                    <Link
                      to={`/queries/${query.id}`}
                      className="mt-2 inline-block text-sm text-indigo-600 font-medium hover:text-indigo-800"
                    >
                      Respond
                    </Link>
                  </div>
                ))}
                {dashboardData.recentQueries.length === 0 && (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No recent inquiries
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <FontAwesomeIcon icon={faTags} className="mr-3 text-emerald-500" />
                  Quick Actions
                </h2>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <QuickActionButton
                  icon={faRulerVertical}
                  title="Inventory"
                  link="/inventory"
                  bgColor="bg-indigo-600"
                />
                <QuickActionButton
                  icon={faUsers}
                  title="Clients"
                  link="/clients"
                  bgColor="bg-purple-600"
                />
                <QuickActionButton
                  icon={faChartLine}
                  title="Reports"
                  link="/reports"
                  bgColor="bg-emerald-600"
                />
                <QuickActionButton
                  icon={faIndustry}
                  title="Profile"
                  link="/profile"
                  bgColor="bg-gray-700"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, iconBg, iconColor, trend, trendUp }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:translate-y-[-4px]">
    <div className="flex items-center">
      <div className={`flex-shrink-0 rounded-full p-3 ${iconBg}`}>
        <FontAwesomeIcon icon={icon} className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="ml-5">
        <div className="mt-1 text-3xl font-semibold text-gray-900">{value}</div>
        <div className="mt-1 text-sm font-medium text-gray-500">{title}</div>
      </div>
    </div>
    {trend && (
      <div className="mt-2 flex items-center text-sm">
        <span className={trendUp ? 'text-green-600' : 'text-red-600'}>
          <FontAwesomeIcon icon={trendUp ? faChartLine : faChartPie} className="mr-1" />
          {trend}
        </span>
        <span className="text-gray-500 ml-2">from last month</span>
      </div>
    )}
  </div>
);

// Quick Action Button Component
const QuickActionButton = ({ icon, title, link, bgColor }) => (
  <Link to={link} className={`${bgColor} hover:opacity-90 text-white rounded-lg p-4 flex flex-col items-center justify-center shadow transition-all hover:shadow-lg hover:translate-y-[-2px]`}>
    <FontAwesomeIcon icon={icon} className="h-6 w-6 mb-2" />
    <span className="text-sm font-medium">{title}</span>
  </Link>
);

export default Dashboard;
